import React, { useState } from "react";
import { ArrowDown } from "lucide-react";

const FRAMEWORKS = [
  {
    id: "jsp936",
    name: "UK MOD JSP 936",
    full: "Dependable AI in Defence (Part 1: Directive, 2024)",
    mappings: [
      {
        principle: "Appropriate human oversight of AI-enabled capability",
        question: "Which AI decisions or actions require a human in the loop?",
        control: "Organisation-defined policies can hold designated interactions or actions for authorised human review before they continue.",
        evidence: "Record the event, the policy applied, the reviewer and the final outcome.",
      },
      {
        principle: "Robust, reliable and assured through the lifecycle",
        question: "How is AI behaviour monitored once it is live?",
        control: "Runtime inspection of inputs, outputs and actions against your policy, with configurable interventions.",
        evidence: "A reviewable trail of interventions supports ongoing assurance activity.",
      },
    ],
  },
  {
    id: "asr",
    name: "MOD Ambitious, Safe, Responsible",
    full: "Five ethical principles: Human-Centricity, Responsibility, Understanding, Bias & Harm Mitigation, Reliability",
    mappings: [
      {
        principle: "Responsibility — accountable people for AI outcomes",
        question: "Who is accountable when AI is used, and how is that shown?",
        control: "Final decisions and escalations stay with named people in Heimdall; norse3 does not decide for you.",
        evidence: "Attributable, time-stamped decisions link the person to the outcome.",
      },
      {
        principle: "Understanding — visibility of what AI is doing",
        question: "Can the organisation see and explain what the AI did?",
        control: "Each handled interaction captures the event, policy, action and reason.",
        evidence: "A structured record supports explanation and review.",
      },
    ],
  },
  {
    id: "nato",
    name: "NATO Principles of Responsible Use",
    full: "Revised AI Strategy & Principles of Responsible Use for AI in Defence",
    mappings: [
      {
        principle: "Governability and accountability",
        question: "Can AI use be governed and, where needed, intervened upon?",
        control: "Policy-driven allow, warn, redact, block, hold and escalate actions around approved AI.",
        evidence: "Interventions and human decisions are recorded for accountability.",
      },
    ],
  },
  {
    id: "iso42001",
    name: "ISO/IEC 42001",
    full: "AI management system standard",
    mappings: [
      {
        principle: "Managed controls and continual improvement for AI",
        question: "Are AI controls defined, operated and reviewed?",
        control: "Configurable policies, role-based access, retention settings and audit records.",
        evidence: "Operational records support management-system review. (norse3 holds ISO 42001.)",
      },
    ],
  },
  {
    id: "nist",
    name: "NIST AI RMF",
    full: "AI Risk Management Framework — Govern, Map, Measure, Manage",
    mappings: [
      {
        principle: "Manage — respond to and monitor AI risks",
        question: "How are identified AI risks handled in operation?",
        control: "Runtime detection and configured responses to designated risks, with escalation paths.",
        evidence: "Records of detections, actions and decisions inform ongoing risk management.",
      },
    ],
  },
];

const STEP_LABELS = ["Principle / expectation", "Operational question", "norse3 control", "Evidence"];

export default function ResponsibleAI() {
  const [active, setActive] = useState(FRAMEWORKS[0].id);
  const cur = FRAMEWORKS.find((f) => f.id === active);

  return (
    <div data-testid="defence-responsible-ai">
      <div className="flex flex-wrap gap-2">
        {FRAMEWORKS.map((f) => {
          const on = f.id === active;
          return (
            <button
              key={f.id}
              onClick={() => setActive(f.id)}
              data-testid={`defence-framework-${f.id}`}
              className={`rounded-full border px-4 py-2 text-sm font-medium transition-colors ${
                on ? "border-primary/40 bg-primary/[0.08] text-foreground" : "border-white/12 bg-navy-900/60 text-muted-foreground hover:border-white/25"
              }`}
            >
              {f.name}
            </button>
          );
        })}
      </div>

      <p className="mt-4 text-sm text-muted-foreground">{cur.full}</p>

      <div className="mt-5 space-y-5">
        {cur.mappings.map((m) => {
          const rows = [m.principle, m.question, m.control, m.evidence];
          return (
            <div key={m.principle} className="rounded-lg border border-white/10 bg-navy-900/60 p-5 sm:p-6">
              <div className="grid gap-2.5">
                {rows.map((r, ri) => (
                  <div key={STEP_LABELS[ri]}>
                    <div className={`rounded-xl border px-4 py-3 ${ri === 2 ? "border-primary/30 bg-primary/[0.05]" : ri === 3 ? "border-signal-green/25 bg-signal-green/[0.04]" : "border-white/10 bg-navy-800/50"}`}>
                      <p className={`text-[0.65rem] font-semibold uppercase tracking-[0.14em] ${ri === 2 ? "text-primary" : ri === 3 ? "text-signal-green" : "text-muted-foreground/70"}`}>{STEP_LABELS[ri]}</p>
                      <p className="mt-1 text-sm text-foreground/90 leading-relaxed">{r}</p>
                    </div>
                    {ri < rows.length - 1 && (
                      <div className="flex justify-center py-0.5" aria-hidden="true"><ArrowDown className="h-3.5 w-3.5 text-muted-foreground/40" /></div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <p className="mt-6 rounded-xl border border-white/10 bg-navy-900/60 px-5 py-4 text-sm text-muted-foreground">
        Framework mappings are educational and do not constitute accreditation, certification, legal advice or confirmation of compliance. norse3 is not MOD-approved, NATO-approved or government-accredited.
      </p>
    </div>
  );
}
